import { useState, useEffect, useCallback } from 'react';
import { customVariablesAPI } from '../../services/api';
import { CustomVariable } from '../../types';

const BOOLEAN_PRESETS = [
  { label: 'Yes / No', values: ['Yes', 'No'] },
  { label: 'True / False', values: ['True', 'False'] },
  { label: 'On / Off', values: ['On', 'Off'] },
  { label: 'Active / Inactive', values: ['Active', 'Inactive'] },
  { label: 'Custom', values: [] },
];

export function useCustomVariablesPage() {
  const [variables, setVariables] = useState<CustomVariable[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [eligible, setEligible] = useState<boolean | null>(null);
  const [showArchived, setShowArchived] = useState(false);

  // Create modal state
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [createName, setCreateName] = useState('');
  const [createType, setCreateType] = useState<'text' | 'dropdown' | 'boolean'>('text');
  const [createDropdownOptions, setCreateDropdownOptions] = useState<string[]>(['']);
  const [createBooleanPreset, setCreateBooleanPreset] = useState(0); // index into BOOLEAN_PRESETS
  const [createBooleanCustomValues, setCreateBooleanCustomValues] = useState<[string, string]>(['', '']);
  const [creating, setCreating] = useState(false);

  // Edit modal state
  const [editVariable, setEditVariable] = useState<CustomVariable | null>(null);
  const [editName, setEditName] = useState('');
  const [editDropdownOptions, setEditDropdownOptions] = useState<string[]>([]);
  const [saving, setSaving] = useState(false);

  const loadVariables = useCallback(async () => {
    try {
      setLoading(true);
      const data = await customVariablesAPI.list(showArchived);
      setVariables(data);
      setError(null);
    } catch (err: any) {
      setError(err?.response?.data?.detail || 'Failed to load fields');
    } finally {
      setLoading(false);
    }
  }, [showArchived]);

  const checkEligibility = useCallback(async () => {
    try {
      const result = await customVariablesAPI.checkEligibility();
      setEligible(result.eligible);
    } catch {
      setEligible(false);
    }
  }, []);

  useEffect(() => {
    checkEligibility();
  }, [checkEligibility]);

  useEffect(() => {
    if (eligible) {
      loadVariables();
    }
  }, [eligible, loadVariables]);

  const handleBooleanPresetChange = (index: number) => {
    setCreateBooleanPreset(index);
    if (index < BOOLEAN_PRESETS.length - 1) {
      // Reset custom values when switching to a preset
      setCreateBooleanCustomValues(['', '']);
    }
  };

  const getBooleanOptions = (): string[] => {
    if (createBooleanPreset < BOOLEAN_PRESETS.length - 1) {
      return BOOLEAN_PRESETS[createBooleanPreset].values;
    }
    // Custom
    return createBooleanCustomValues.filter(v => v.trim());
  };

  const handleCreate = async () => {
    if (!createName.trim()) return;
    try {
      setCreating(true);
      let options: string[] | undefined;
      if (createType === 'dropdown') {
        options = createDropdownOptions.filter(o => o.trim());
      } else if (createType === 'boolean') {
        options = getBooleanOptions();
        if (options.length < 2) {
          setError('Boolean fields require two values');
          setCreating(false);
          return;
        }
      }
      await customVariablesAPI.create({
        name: createName.trim(),
        variable_type: createType,
        dropdown_options: options,
      });
      setShowCreateModal(false);
      setCreateName('');
      setCreateType('text');
      setCreateDropdownOptions(['']);
      setCreateBooleanPreset(0);
      setCreateBooleanCustomValues(['', '']);
      loadVariables();
    } catch (err: any) {
      setError(err?.response?.data?.detail || 'Failed to create field');
    } finally {
      setCreating(false);
    }
  };

  const openEdit = (v: CustomVariable) => {
    setEditVariable(v);
    setEditName(v.name);
    if (v.variable_type === 'boolean' && v.dropdown_options && v.dropdown_options.length === 2) {
      setEditDropdownOptions([...v.dropdown_options]);
    } else {
      setEditDropdownOptions(v.dropdown_options || []);
    }
  };

  const handleSaveEdit = async () => {
    if (!editVariable) return;
    try {
      setSaving(true);
      const updates: any = {};
      if (editName.trim() !== editVariable.name) updates.name = editName.trim();
      if (editVariable.variable_type === 'dropdown') {
        updates.dropdown_options = editDropdownOptions.filter(o => o.trim());
      }
      if (editVariable.variable_type === 'boolean') {
        updates.dropdown_options = editDropdownOptions.filter(o => o.trim());
      }
      await customVariablesAPI.update(editVariable.id, updates);
      setEditVariable(null);
      loadVariables();
    } catch (err: any) {
      setError(err?.response?.data?.detail || 'Failed to update field');
    } finally {
      setSaving(false);
    }
  };

  const handleArchive = async (variableId: string) => {
    try {
      await customVariablesAPI.update(variableId, { is_archived: true });
      loadVariables();
    } catch (err: any) {
      setError(err?.response?.data?.detail || 'Failed to archive field');
    }
  };

  const handleRestore = async (variableId: string) => {
    try {
      await customVariablesAPI.update(variableId, { is_archived: false });
      loadVariables();
    } catch (err: any) {
      setError(err?.response?.data?.detail || 'Failed to restore field');
    }
  };

  return {
    variables,
    loading,
    error,
    eligible,
    showArchived,
    setShowArchived,
    showCreateModal,
    setShowCreateModal,
    createName,
    setCreateName,
    createType,
    setCreateType,
    createDropdownOptions,
    setCreateDropdownOptions,
    createBooleanPreset,
    setCreateBooleanPreset,
    createBooleanCustomValues,
    setCreateBooleanCustomValues,
    handleBooleanPresetChange,
    creating,
    handleCreate,
    editVariable,
    setEditVariable,
    editName,
    setEditName,
    editDropdownOptions,
    setEditDropdownOptions,
    saving,
    openEdit,
    handleSaveEdit,
    handleArchive,
    handleRestore,
  };
}
